/**
 * @typedef {import('../types/support').SupportIssue} SupportIssue
 * @typedef {import('../types/support').SupportComment} SupportComment
 * @typedef {import('../types/support').CreateIssueResponse} CreateIssueResponse
 * @typedef {import('../types/support').CreateCommentResponse} CreateCommentResponse
 * @typedef {import('../types/support').CreateAttachmentResponse} CreateAttachmentResponse
 * @typedef {import('../types/support').DeleteAttachmentResponse} DeleteAttachmentResponse
 * @typedef {import('../types/support').GetIssuesResponse} GetIssuesResponse
 * @typedef {import('../types/support').GetIssueResponse} GetIssueResponse
 * @typedef {import('../types/support').GetCommentsResponse} GetCommentsResponse
 */

const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';

/** Lê o token preferindo auth_token, com fallback para access_token. */
function getToken() {
  return (
    localStorage.getItem('auth_token') ||
    localStorage.getItem('access_token') ||
    null
  );
}

function handleUnauthorized() {
  localStorage.removeItem('auth_token');
  localStorage.removeItem('access_token');
  localStorage.removeItem('user_data');
  localStorage.removeItem('user');
  localStorage.removeItem('admin_user');
  window.location.href = '/admin/login';
}

/**
 * Faz uma requisição autenticada com fetch.
 * @param {string} path
 * @param {RequestInit} [init]
 * @param {AbortSignal} [signal]
 * @returns {Promise<Response>}
 */
async function authFetch(path, init = {}, signal) {
  const token = getToken();
  const headers = new Headers(/** @type {HeadersInit} */ (init.headers || {}));
  if (token) headers.set('Authorization', `Bearer ${token}`);

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    headers,
    signal,
  });

  if (response.status === 401) {
    handleUnauthorized();
    throw new Error('Não autorizado');
  }

  return response;
}

/**
 * Valida envelope de resposta Jira.
 * @template {{ error?: string | null; response_status?: number | null }} T
 * @param {T} resp
 * @param {string} fallbackMessage
 */
function assertJiraEnvelope(resp, fallbackMessage) {
  if (resp.error) throw new Error(resp.error);
  if (typeof resp.response_status === 'number' && resp.response_status >= 400) {
    throw new Error(resp.error || `${fallbackMessage} (${resp.response_status})`);
  }
}

/**
 * Extrai o nome do arquivo do header Content-Disposition.
 * @param {string | null} contentDisposition
 * @returns {string | undefined}
 */
function parseFilenameFromContentDisposition(contentDisposition) {
  if (!contentDisposition) return undefined;

  const starMatch = contentDisposition.match(/filename\*=([^;]+)/i);
  if (starMatch?.[1]) {
    const raw = starMatch[1].trim();
    const utfIdx = raw.indexOf("''");
    const encoded = utfIdx >= 0 ? raw.slice(utfIdx + 2) : raw;
    try {
      return decodeURIComponent(encoded.replace(/^"(.*)"$/, '$1'));
    } catch {
      return encoded.replace(/^"(.*)"$/, '$1');
    }
  }

  const simpleMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  if (simpleMatch?.[1]) return simpleMatch[1].trim();

  return undefined;
}

/**
 * Busca todos os chamados com paginação automática.
 * @param {{ signal?: AbortSignal; pageSize?: number }} [opts]
 * @returns {Promise<SupportIssue[]>}
 */
async function fetchAllIssues({ signal, pageSize = 100 } = {}) {
  const all = /** @type {SupportIssue[]} */ ([]);
  let page = 1;
  const MAX_PAGES = 500;

  while (page <= MAX_PAGES) {
    const params = new URLSearchParams({
      page: String(page),
      count: String(pageSize),
    });

    const response = await authFetch(`/admin/support/issues?${params}`, {}, signal);

    if (!response.ok) {
      if (response.status === 401) throw new Error('Não autorizado');
      throw new Error(`Erro ao buscar chamados (${response.status})`);
    }

    /** @type {GetIssuesResponse} */
    const data = await response.json();
    assertJiraEnvelope(data, 'Erro ao buscar chamados');

    const batch = data.issues || [];
    all.push(...batch);

    if (batch.length < pageSize) break;
    page += 1;
  }

  return all;
}

/**
 * Cria um chamado.
 * @param {object} body
 * @returns {Promise<CreateIssueResponse>}
 */
async function createIssue(body) {
  const response = await authFetch('/admin/support/issues', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!response.ok && response.status !== 401) {
    throw new Error(`Erro ao criar chamado (${response.status})`);
  }

  /** @type {CreateIssueResponse} */
  const data = await response.json();
  assertJiraEnvelope(data, 'Erro ao criar chamado');
  return data;
}

/**
 * Busca um chamado pelo external_id.
 * @param {string} issueExternalId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<SupportIssue | null>}
 */
async function getIssue(issueExternalId, { signal } = {}) {
  const response = await authFetch(
    `/admin/support/issues/${issueExternalId}`,
    {},
    signal,
  );

  if (response.status === 404) return null;
  if (!response.ok) throw new Error(`Erro ao buscar chamado (${response.status})`);

  /** @type {GetIssueResponse} */
  const data = await response.json();
  assertJiraEnvelope(data, 'Erro ao buscar chamado');
  return data.issue || null;
}

/**
 * Busca comentários de um chamado.
 * @param {string} issueExternalId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<SupportComment[]>}
 */
async function getComments(issueExternalId, { signal } = {}) {
  const response = await authFetch(
    `/admin/support/issues/${issueExternalId}/comments`,
    {},
    signal,
  );

  if (!response.ok) throw new Error(`Erro ao buscar comentários (${response.status})`);

  /** @type {GetCommentsResponse} */
  const data = await response.json();
  assertJiraEnvelope(data, 'Erro ao buscar comentários');
  return data.comments || [];
}

/**
 * Cria um comentário num chamado.
 * @param {string} issueExternalId
 * @param {object} body
 * @returns {Promise<CreateCommentResponse>}
 */
async function createComment(issueExternalId, body) {
  const response = await authFetch(
    `/admin/support/issues/${issueExternalId}/comments`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    },
  );

  if (!response.ok && response.status !== 401) {
    throw new Error(`Erro ao enviar comentário (${response.status})`);
  }

  /** @type {CreateCommentResponse} */
  const data = await response.json();
  assertJiraEnvelope(data, 'Erro ao enviar comentário');
  return data;
}

/**
 * Faz upload de um anexo para um chamado.
 * @param {string} issueExternalId
 * @param {File} file
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<CreateAttachmentResponse>}
 */
async function uploadAttachment(issueExternalId, file, { signal } = {}) {
  const formData = new FormData();
  formData.append('file', file);

  const response = await authFetch(
    `/admin/support/issues/${issueExternalId}/attachments`,
    { method: 'POST', body: formData },
    signal,
  );

  if (!response.ok && response.status !== 401) {
    throw new Error(`Erro ao enviar anexo (${response.status})`);
  }

  /** @type {CreateAttachmentResponse} */
  const data = await response.json();
  assertJiraEnvelope(data, 'Erro ao enviar anexo');
  return data;
}

/**
 * Exclui um anexo.
 * @param {string} issueExternalId
 * @param {string} attachmentExternalId
 * @returns {Promise<DeleteAttachmentResponse>}
 */
async function deleteAttachment(issueExternalId, attachmentExternalId) {
  const response = await authFetch(
    `/admin/support/issues/${issueExternalId}/attachments/${attachmentExternalId}`,
    { method: 'DELETE' },
  );

  if (!response.ok && response.status !== 401) {
    throw new Error(`Erro ao excluir anexo (${response.status})`);
  }

  /** @type {DeleteAttachmentResponse} */
  const data = await response.json();
  return data;
}

/**
 * Baixa um anexo e retorna blob + metadados.
 * @param {string} issueExternalId
 * @param {string} attachmentExternalId
 * @param {{ signal?: AbortSignal }} [opts]
 * @returns {Promise<{ blob: Blob; filename?: string; contentType?: string }>}
 */
async function getAttachment(issueExternalId, attachmentExternalId, { signal } = {}) {
  const response = await authFetch(
    `/admin/support/issues/${issueExternalId}/attachments/${attachmentExternalId}`,
    {},
    signal,
  );

  if (!response.ok) throw new Error(`Erro ao baixar anexo (${response.status})`);

  const contentDisposition = response.headers.get('Content-Disposition');
  const contentType = response.headers.get('Content-Type') || undefined;
  const filename = parseFilenameFromContentDisposition(contentDisposition);
  const blob = await response.blob();

  return { blob, filename, contentType };
}

const supportIssuesApi = {
  fetchAllIssues,
  createIssue,
  getIssue,
  getComments,
  createComment,
  uploadAttachment,
  deleteAttachment,
  getAttachment,
};

export default supportIssuesApi;
